import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, CheckCircle, MessageSquare, Send } from 'lucide-react';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useSiteLanguage();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', subject: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/enquiries`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        type: 'contact',
      });
      setSubmitted(true);
    } catch (err) {
      setError(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  return (
    <div className="min-h-screen">
      <Seo
        title="Contact Us"
        description="Speak to an Atlas Academy course adviser by phone, WhatsApp, or email. We're here to help you choose the right qualification."
        path="/contact"
      />
      {/* Hero */}
      <div className="bg-[#0f2b4a] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-blue-300 text-sm font-medium mb-2">{t('contact.hero.eyebrow')}</p>
          <h1 className="text-3xl lg:text-5xl font-extrabold mb-4">{t('contact.hero.title')}</h1>
          <p className="text-blue-100/70 max-w-xl text-lg leading-relaxed">
            {t('contact.hero.desc')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact info */}
          <div className="space-y-5">
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
              <h2 className="font-bold text-gray-900 mb-5">{t('contact.info.title')}</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <Phone size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">{t('contact.info.phone')}</p>
                    <a href="tel:+442033555646" dir="ltr" className="text-sm font-medium text-gray-800 hover:text-blue-600 transition-colors block">+44 (0) 20 3355 5646</a>
                    <a href="tel:+447859151818" dir="ltr" className="text-sm text-gray-600 hover:text-blue-600 transition-colors block">+44 (0) 78 5915 1818</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <Mail size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">{t('contact.info.email')}</p>
                    <a href="mailto:info@atlas-academy.co.uk" className="text-sm font-medium text-gray-800 hover:text-blue-600 transition-colors">
                      info@atlas-academy.co.uk
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <MapPin size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">{t('contact.info.address')}</p>
                    <p className="text-sm text-gray-800">
                      Atlas Academy<br />
                      123 Education House<br />
                      London, EC1A 1BB
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <Clock size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">{t('contact.info.hours')}</p>
                    <p className="text-sm text-gray-800">{t('contact.info.hoursValue')}</p>
                    <p className="text-sm text-gray-500">{t('contact.info.portalHours')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
              <MessageSquare size={22} className="text-blue-600 mb-3" />
              <h3 className="font-bold text-gray-900 text-sm mb-1">{t('contact.prefer.title')}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">
                {t('contact.prefer.desc')}
              </p>
              <a href="tel:+442033555646" className="mt-3 block text-blue-700 font-semibold text-sm hover:underline">
                {t('contact.prefer.cta')}
              </a>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 lg:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">{t('contact.success.title')}</h2>
                  <p className="text-gray-500 text-sm max-w-sm mx-auto">
                    {t('contact.success.desc')}
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' }); }}
                    className="mt-6 text-blue-600 hover:underline text-sm font-medium"
                  >
                    {t('contact.success.another')}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">{t('contact.form.title')}</h2>
                  <p className="text-gray-500 text-sm mb-6">{t('contact.form.desc')}</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact.form.firstName')} *</label>
                        <input required value={formData.firstName} onChange={update('firstName')}
                          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Jane" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact.form.lastName')} *</label>
                        <input required value={formData.lastName} onChange={update('lastName')}
                          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Smith" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact.form.emailAddress')} *</label>
                        <input required type="email" value={formData.email} onChange={update('email')}
                          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="jane@example.com" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact.form.phoneNumber')}</label>
                        <input type="tel" dir="ltr" value={formData.phone} onChange={update('phone')}
                          className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="+44 7911 123456" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact.form.subject')} *</label>
                      <select required value={formData.subject} onChange={update('subject')}
                        className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
                        <option value="">{t('contact.form.selectTopic')}</option>
                        <option>{t('contact.form.topic.courseEnquiry')}</option>
                        <option>{t('contact.form.topic.enrolment')}</option>
                        <option>{t('contact.form.topic.fees')}</option>
                        <option>{t('contact.form.topic.support')}</option>
                        <option>{t('contact.form.topic.tech')}</option>
                        <option>{t('contact.form.topic.general')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact.form.message')} *</label>
                      <textarea required rows={5} value={formData.message} onChange={update('message')}
                        className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                        placeholder="Tell us how we can help..."
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-8 py-3 rounded-lg font-semibold text-sm transition-colors"
                    >
                      {loading ? t('contact.form.sending') : (<><Send size={15} /> {t('contact.form.send')}</>)}
                    </button>
                    {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

